import { below } from './depth.js';
/**
 * The stretch on the ground that `time` falls in, or the next one after it. "Next" matters
 * when the seabed is pinned from a sounding rather than from a moment: the boat is still
 * afloat, and the question is when it will touch.
 */
function windowAtOrAfter(windows, time) {
    return windows.find(w => w.to >= time) ?? null;
}
function span(w) {
    return w ? w.to - w.from : 0;
}
/** The drying-out picture for a given seabed level */
export function dryingAt(points, seabed, draughtCm, time) {
    const touching = below(points, seabed + draughtCm);
    const aground = time === undefined ? (touching[0] ?? null) : windowAtOrAfter(touching, time);
    const dryStretches = below(points, seabed);
    const dry = aground ? dryStretches.find(d => d.from >= aground.from - 1 && d.to <= aground.to + 1) ?? null : (dryStretches[0] ?? null);
    return { seabed, aground, dry, dryMs: span(dry), agroundMs: span(aground) };
}
/**
 * The skipper names the moment of grounding, and how much water is under the keel then --
 * normally none, the keel just touching, but a sounder reading works too. That fixes the
 * seabed, and the rest follows (obj_DryingOut.pas:210).
 */
export function dryingFromTime(points, time, draughtCm, waterUnderKeelCm = 0) {
    const level = levelAt(points, time);
    if (level === null)
        return null;
    // at that moment the water is the draught plus whatever is left under the keel
    return dryingAt(points, level - draughtCm - waterUnderKeelCm, draughtCm, time);
}
/**
 * The skipper names how long to sit dry, and the seabed follows: the higher the spot, the
 * longer the water is away. Raising it is monotone in the dry time, so this bisects where the
 * Delphi walks up a centimetre at a time (obj_DryingOut.pas:239, whose own comment asks for
 * exactly this).
 */
export function dryingForDuration(points, around, dryMs, draughtCm) {
    if (points.length === 0)
        return null;
    // between the low water and the lower of the high waters either side of the moment
    let lo = Math.min(...points.map(p => p.level));
    let hi = Math.max(...points.map(p => p.level));
    if (dryingAt(points, hi, draughtCm, around).dryMs < dryMs)
        return dryingAt(points, hi, draughtCm, around);
    for (let i = 0; i < 40 && hi - lo > 0.5; i++) {
        const mid = (lo + hi) / 2;
        if (dryingAt(points, mid, draughtCm, around).dryMs >= dryMs)
            hi = mid;
        else
            lo = mid;
    }
    return dryingAt(points, Math.round(hi), draughtCm, around);
}
/** Linear interpolation between grid points; null outside the curve */
export function levelAt(points, time) {
    if (points.length === 0)
        return null;
    let lo = 0;
    let hi = points.length - 1;
    if (time < points[lo].time || time > points[hi].time)
        return null;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (points[mid].time <= time)
            lo = mid;
        else
            hi = mid;
    }
    const a = points[lo];
    const b = points[hi];
    if (b.time === a.time)
        return a.level;
    return a.level + ((b.level - a.level) * (time - a.time)) / (b.time - a.time);
}
/** Is the tide falling at this moment? Grounding happens on a falling tide. */
export function isFalling(points, time) {
    const now = levelAt(points, time);
    const soon = levelAt(points, time + 600_000);
    return now !== null && soon !== null && soon < now;
}
/**
 * Afloat, aground and dry across a whole span, in order (obj_Graph.pas:850).
 *
 * Every episode, not just the one the skipper asked about: a boat left on a spot it grounds
 * on at one low water will ground there again at the next, and a bar that says otherwise for
 * the rest of the day is telling a comfortable lie.
 */
export function dryingTimeline(points, seabed, draughtCm, from, to) {
    const edges = new Set([from, to]);
    for (const level of [seabed, seabed + draughtCm]) {
        for (const w of below(points, level)) {
            for (const edge of [w.from, w.to])
                if (edge > from && edge < to)
                    edges.add(edge);
        }
    }
    const times = [...edges].sort((a, b) => a - b);
    const phases = [];
    for (let i = 0; i < times.length - 1; i++) {
        const start = times[i];
        const end = times[i + 1];
        const level = levelAt(points, (start + end) / 2);
        const state = level === null || level > seabed + draughtCm ? 'afloat' : level <= seabed ? 'dry' : 'aground';
        const last = phases[phases.length - 1];
        if (last && last.state === state)
            last.to = end;
        else
            phases.push({ state, from: start, to: end });
    }
    return phases;
}
//# sourceMappingURL=drying.js.map